import { Button } from "@ui/Button";
import { navigateToAccountsPath } from "@ui/layouts/AccountLayout/accountsReferrer";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { ProgressBar } from "@ui/ProgressBar";
import { HStack, VStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import { timeAgo } from "@utils/time";
import { Match, onMount, Show, Switch } from "solid-js";
import { formatRemainingOfGranted } from "~/modules/accounts/usage/components/grantFormat";
import { useStudioBilling } from "~/modules/shared/utils/useStudioBilling";

declare const pageVars: {
	userName: string;
};

type ResourceRowProps = {
	label: string;
	unit: string;
	symbol: string;
	remaining: number;
	granted: number;
	progressVariant: "primary" | "info" | "purple";
	iconColor: "primary" | "purple";
};

const ResourceRow = (props: ResourceRowProps) => {
	const pct = () =>
		props.granted > 0
			? Math.min(100, (props.remaining / props.granted) * 100)
			: 0;

	return (
		<div class="cost-dropdown__resource-row">
			<HStack spacing="small" align="center" justify="space-between">
				<HStack spacing="small" align="center">
					<MaterialSymbol
						symbol={props.symbol}
						color={props.iconColor}
						relativeSize="smaller"
					/>
					<Text size="small" weight="bold" color="ink">
						{props.label}
					</Text>
				</HStack>
				<Text size="small" color="fg2" family="mono">
					{formatRemainingOfGranted(props.remaining, props.granted, props.unit)}
				</Text>
			</HStack>
			<ProgressBar value={pct()} variant={props.progressVariant} />
		</div>
	);
};

type AccountCostPanelProps = {
	/** Studio accounts meter VIUs; AskGVT consumer usage is credits-only. */
	showVius?: boolean;
};

export const AccountCostPanel = (props: AccountCostPanelProps) => {
	const showVius = () => props.showVius !== false;
	const { subscription, grants, refetchGrants } = useStudioBilling();

	// The panel only mounts when the popover opens. Refresh so the figure is
	// the same fetch a Usage page load would make, not a leftover from earlier
	// on this page.
	onMount(() => {
		void refetchGrants();
	});

	// Keep the last payload on screen while a refresh is in flight — otherwise
	// opening the popover after the first load flashes "Loading usage...".
	const data = () => (grants.error ? undefined : (grants.latest ?? grants()));
	const isInitialLoad = () => grants.loading && grants.latest === undefined;

	const planName = () => `${subscription()?.plan_name ?? "Free"} plan`;
	const vius = () => data()?.totals?.vius;
	const credits = () => data()?.totals?.credits;
	const lastUpdated = () => {
		const updated = data()?.last_updated;
		return updated
			? `Last updated ${timeAgo(updated)}`
			: "Last updated recently";
	};

	return (
		<div class="cost-dropdown__panel cost-dropdown__panel--account">
			<Switch>
				<Match when={isInitialLoad()}>
					<Text as="span" size="xsmall" color="fg3">
						Loading usage...
					</Text>
				</Match>
				<Match when={grants.error}>
					<Text as="span" size="xsmall" color="fg3">
						Usage unavailable.
					</Text>
				</Match>
				<Match when={data()}>
					<VStack spacing="medium">
						<div class="cost-dropdown__header">
							<MaterialSymbol
								symbol="account_circle"
								container={{
									background: "var(--gvt-teal-light)",
									borderRadius: "0.5rem",
									size: "2rem",
								}}
								color="primary"
							/>
							<VStack spacing="small">
								<Text
									family="mono"
									size="xsmall"
									weight="bold"
									color="fg3"
									transform="uppercase"
								>
									Account usage
								</Text>
								<Text size="small" color="fg2">
									{pageVars.userName} · {planName()}
								</Text>
							</VStack>
						</div>

						<VStack spacing="medium">
							<Show when={showVius()}>
								<ResourceRow
									label="VIUs"
									unit="VIUs"
									symbol="play_circle"
									remaining={vius()?.remaining ?? 0}
									granted={vius()?.granted ?? 0}
									progressVariant="purple"
									iconColor="purple"
								/>
							</Show>
							<ResourceRow
								label="Credits"
								unit="credits"
								symbol="toll"
								remaining={credits()?.remaining ?? 0}
								granted={credits()?.granted ?? 0}
								progressVariant="info"
								iconColor="primary"
							/>
						</VStack>

						<Text as="span" size="xsmall" color="fg4">
							{lastUpdated()}
						</Text>

						<Button
							variant="secondary"
							theme="monochrome"
							class="cost-dropdown__view-full"
							symbol="monitoring"
							symbolRelativeSize={1.25}
							onClick={() => navigateToAccountsPath("/account/usage")}
						>
							<span class="cost-dropdown__view-full-text">View full usage</span>
							<MaterialSymbol
								symbol="arrow_forward"
								color="fg3"
								relativeSize="smaller"
							/>
						</Button>
					</VStack>
				</Match>
			</Switch>
		</div>
	);
};
